import React from 'react';
import { Calendar, Settings, Plus } from 'lucide-react';
import SettingsModal from './SettingsModal';
import StatusToast from './StatusToast';
import { formatSignedInAs } from '../utils/formatting';

// Extracted from App.js (App.js modularization, Sep 2026) — the
// step === 'choose-calendar' screen, shown when a plain member belongs
// to more than one calendar and needs to pick which one to open.
//
// Added 5 Sep 2026: this screen never had the "Signed in as" line or a
// Settings button — unlike member-calendar, which got both on 4 Sep — simply
// because choose-calendar had never actually been reachable/tested before
// (the plain-member test account only belonged to one calendar until now).
// Brought in line with member-calendar's header pattern.
const ChooseCalendarStep = ({
  memberCalendars,
  onSelectCalendar,
  isAuthenticated,
  onSignOut,
  statusMessage,
  userEmail,
  preferredName,
  showSettings,
  setShowSettings,
  settingsInitialTab,
  setSettingsInitialTab,
  apiBaseUrl,
  authToken,
  onStatusMessage,
  userTier,
  firstName,
  lastName,
  onSaveProfile,
  // (28 Oct 2026, decision 153) Someone with the right to create calendars who owns none.
  canCreateCalendar = false,
  onNewCalendar,
}) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
      <StatusToast statusMessage={statusMessage} />
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex flex-wrap items-center justify-between gap-y-2">
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 p-2 rounded-lg">
              <Calendar className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">Telematrix Calendar Hub</h1>
              <p className="text-xs text-gray-500">Choose a calendar</p>
              {userEmail && (
                <p className="text-xs text-gray-400">Signed in as: {formatSignedInAs(preferredName, firstName, lastName, userEmail)}</p>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* (28 Oct 2026, decision 153) Opens Create Calendar; Cancel returns here. */}
            {canCreateCalendar && onNewCalendar && (
              <button
                onClick={onNewCalendar}
                className="flex items-center gap-1 bg-blue-600 text-white px-2 py-1.5 rounded-lg hover:bg-blue-700 transition text-xs font-medium whitespace-nowrap"
              >
                <Plus className="w-3.5 h-3.5" />
                New Calendar
              </button>
            )}
            {isAuthenticated && (
              <button
                onClick={onSignOut}
                className="px-2 py-1.5 text-xs text-red-600 hover:text-red-700 font-medium border border-red-300 rounded-lg hover:bg-red-50 transition whitespace-nowrap"
              >
                Sign Out
              </button>
            )}
            <button
              onClick={() => setShowSettings(true)}
              className="flex items-center gap-1 px-2 py-1.5 text-gray-600 hover:text-gray-900 text-xs whitespace-nowrap"
            >
              <Settings className="w-3.5 h-3.5" />
              Settings
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">
        <p className="text-sm text-gray-600 mb-4">You're a member of {memberCalendars.length} calendars. Which one would you like to open?</p>
        <div className="space-y-3">
          {memberCalendars.map((cal) => (
            <button
              key={cal.id}
              onClick={() => onSelectCalendar(cal)}
              className="w-full text-left bg-white border border-gray-200 rounded-xl p-4 hover:border-blue-400 hover:shadow-md transition flex items-center justify-between"
            >
              <div>
                <p className="font-semibold text-gray-800">{cal.name}</p>
                <p className="text-xs text-gray-500">{cal.timezone}</p>
              </div>
              <span className="text-blue-600 text-sm font-medium">Open →</span>
            </button>
          ))}
        </div>
      </div>

      {showSettings && (
        <SettingsModal
          isOpen={showSettings}
          onClose={() => { setShowSettings(false); setSettingsInitialTab('profile'); }}
          userEmail={userEmail}
          apiBaseUrl={apiBaseUrl}
          authToken={authToken}
          onStatusMessage={onStatusMessage}
          userTier={userTier}
          initialTab={settingsInitialTab}
          currentCalendar={null}
          firstName={firstName}
          lastName={lastName}
          onSaveProfile={onSaveProfile}
          restrictToProfileTabs={true}
        />
      )}
    </div>
  );
};

export default ChooseCalendarStep;
